import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  CardContent,
  CardDescription,
  CardFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  LoadingSpinner,
  ScrollArea,
  TruncatedText,
} from '@ais-ui/ui-core';
import React, { useMemo, useState } from 'react';
import { cn, isNonEmpty } from '@ais-ui/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  PiCalendarThin,
  PiCalendarXThin,
  PiDotsThreeVertical,
} from 'react-icons/pi';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { ScheduleWithDate, useMessagesScheduledList } from '@ais-ui/api';
import { filterMessageSchedules } from './utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { Schedule } from '@ais-ui/api-spec/config';
import { MessageActionsMenu } from './MessageActionsMenu';
import { DeleteMessageSchedule } from './mutations/DeleteMessageSchedule';
import { EditMessageSchedule } from './mutations/EditMessageSchedule';

interface MessageScheduleGridItemProps {
  label: string;
  value: string | undefined | null;
}
const MessageScheduleGridItem = ({
  label,
  value,
}: MessageScheduleGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

interface MessageScheduleDropDownMenuProps {
  messageSchedule: ScheduleWithDate;
  onDelete: () => void;
  onEdit: (event: React.FormEvent) => void;
  className?: string;
}

const MessageScheduleDropDownMenu = ({
  messageSchedule,
  onDelete,
  onEdit,
  className,
}: MessageScheduleDropDownMenuProps) => {
  const { messages } = useLocalization();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button
          id="schedule-messages-dropdown-button"
          size="icon"
          variant="ghost"
          className="size-7"
        >
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function secondsToReadableInterval(seconds: number | undefined) {
  if (!seconds) {
    return 'Unknown';
  }

  const minute = 60;
  const hour = 60 * minute;

  const hours = Math.floor(seconds / hour);
  const minutes = Math.floor((seconds % hour) / minute);
  const remainingSeconds = seconds % minute;

  let readableString = 'Every ';

  if (hours > 0) {
    readableString += hours === 1 ? `${hours} hour` : `${hours} hours`;
  }

  if (minutes > 0) {
    if (hours > 0) {
      readableString += ' and ';
    }
    readableString +=
      minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
  }

  if (hours === 0 && minutes === 0) {
    readableString +=
      remainingSeconds === 1
        ? `${remainingSeconds} second`
        : `${remainingSeconds} seconds`;
  }

  return readableString;
}

interface MessageScheduleItemProps {
  messageSchedule: ScheduleWithDate;
}

const MessageScheduleItem = ({ messageSchedule }: MessageScheduleItemProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onEdit = () => {
    setShowEditDialog(true);
  };

  const scheduleMessage = () => {
    if (!messageSchedule?.schedule || messageSchedule.schedule.length === 0) {
      return messages.messagesNoSchedules;
    }

    const currentDate = new Date();
    const startDate = messageSchedule.schedule[0].start as Date;
    const endDate = messageSchedule.schedule[0].end as Date;

    if (startDate > currentDate) {
      return `Starts ${formatDateTimeWithTimeZone(startDate)}`;
    }

    if (endDate >= currentDate) {
      return `Ends ${formatDateTimeWithTimeZone(endDate)}`;
    }

    return messages.messagesNoSchedules;
  };

  return (
    <AccordionItem
      key={messageSchedule.uid}
      value={messageSchedule.uid ? messageSchedule.uid : uuidv4()}
      className={cn('px-4')}
    >
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <AccordionTrigger className="w-full py-2" hideChevron={true}>
            <div className="flex items-center w-full min-w-0">
              <div className="relative overflow-visible flex-shrink-0">
                {!messageSchedule.enabled && (
                  <PiCalendarXThin className="h-6 w-6" />
                )}
                {messageSchedule.enabled && (
                  <PiCalendarThin className="h-6 w-6" />
                )}
              </div>
              <div className="ml-4 space-y-1 min-w-0 flex-1">
                <TruncatedText
                  text={
                    messageSchedule.schedMsgName
                      ? messageSchedule.schedMsgName
                      : messages.messagesNoName
                  }
                  className="text-sm text-left font-medium leading-none group-hover:underline"
                  maxWidth="224px"
                />
                <p className="text-sm text-left text-muted-foreground truncate">
                  {scheduleMessage()}
                </p>
              </div>
            </div>
          </AccordionTrigger>
        </div>
        <MessageScheduleDropDownMenu
          messageSchedule={messageSchedule}
          onDelete={onDelete}
          onEdit={onEdit}
        ></MessageScheduleDropDownMenu>
      </div>

      <AccordionContent>
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <MessageScheduleGridItem
              label="Enabled"
              value={messageSchedule.enabled ? 'Yes' : 'No'}
            />
            <MessageScheduleGridItem
              label="Owner"
              value={messageSchedule.owner}
            />
            <MessageScheduleGridItem
              label="Broadcast"
              value={secondsToReadableInterval(
                messageSchedule.broadcastInterval,
              )}
            />
            <MessageScheduleGridItem
              label="Type"
              value={
                messageSchedule.type === 'safety' ? 'Safety' : 'Non-safety'
              }
            />
          </div>
          <div className="grid gap-4">
            <Label className="text-lg">Schedules</Label>
            {messageSchedule?.schedule &&
              messageSchedule.schedule.map((schedule, index) => {
                return (
                  <div className="space-y-2" key={index}>
                    <Label>Schedule {index + 1}</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <MessageScheduleGridItem
                        label="Start"
                        value={formatDateTimeWithTimeZone(schedule.start)}
                      />
                      <MessageScheduleGridItem
                        label="End"
                        value={formatDateTimeWithTimeZone(schedule.end)}
                      />
                    </div>
                  </div>
                );
              })}
            <MessageScheduleGridItem
              label="Text"
              value={messageSchedule.text}
            />
          </div>
        </div>
      </AccordionContent>
      {showDeleteDialog && (
        <DeleteMessageSchedule
          messageSchedule={messageSchedule}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
        ></DeleteMessageSchedule>
      )}
      {showEditDialog && (
        <EditMessageSchedule
          messageSchedule={messageSchedule}
          setShowDialog={setShowEditDialog}
        ></EditMessageSchedule>
      )}
    </AccordionItem>
  );
};

export const MessageSchedules = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const { data, isLoading } = useMessagesScheduledList();

  const messageSchedules = data?.records || [];

  const filteredMessageSchedules = useMemo(() => {
    if (!data) {
      return [];
    }

    const messageSchedules = data.records || [];
    if (search === '') {
      return messageSchedules;
    }

    return filterMessageSchedules(messageSchedules, search);
  }, [data, search]);

  return (
    <>
      <CardContent className="space-y-4">
        <CardDescription>{messages.messagesSubtitle}</CardDescription>
        <Input
          id="search"
          placeholder={messages.messagesSearchMessageSchedules}
          className="h-10"
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {!isLoading && messageSchedules?.length === 0 && (
          <NoItemsEmptyStateList
            entity="message schedule"
            srcImage="empty_list_bro.png"
          ></NoItemsEmptyStateList>
        )}
        {isLoading && <LoadingSpinner></LoadingSpinner>}
        {!isLoading && messageSchedules?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <ScrollArea
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              observeResize={true}
            >
              {filteredMessageSchedules.map((messageSchedule) => {
                return (
                  <MessageScheduleItem
                    key={messageSchedule.uid}
                    messageSchedule={messageSchedule}
                  />
                );
              })}
            </ScrollArea>
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end">
        <MessageActionsMenu buttonText="Actions" />
      </CardFooter>
    </>
  );
};
